import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { X } from 'lucide-react';

const AppointmentForm = ({ appointment, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    property_id: appointment?.property_id || '',
    client_id: appointment?.client_id || '',
    agent_id: appointment?.agent_id || '',
    notary_id: appointment?.notary_id || '',
    appointment_type: appointment?.appointment_type || 'Property Visit',
    status: appointment?.status || 'scheduled',
    scheduled_date: appointment?.scheduled_date ? 
      new Date(appointment?.scheduled_date)?.toISOString()?.slice(0, 16) : '',
    duration_minutes: appointment?.duration_minutes || 60,
    location: appointment?.location || '',
    notes: appointment?.notes || '',
    client_notes: appointment?.client_notes || '',
    agent_notes: appointment?.agent_notes || ''
  });

  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [notaries, setNotaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchFormData();
  }, []);

  const fetchFormData = async () => {
    try {
      const [propertiesRes, clientsRes, agentsRes, notariesRes] = await Promise.all([
        supabase?.from('properties')?.select('id, title, address, status')?.eq('status', 'available'),
        supabase?.from('user_profiles')?.select('id, full_name, email')?.eq('role', 'client'),
        supabase?.from('user_profiles')?.select('id, full_name, email')?.eq('role', 'agent'),
        supabase?.from('notaries')?.select('id, license_number, user:user_profiles(id, full_name, email)')?.eq('is_verified', true)
      ]);

      setProperties(propertiesRes?.data || []);
      setClients(clientsRes?.data || []);
      setAgents(agentsRes?.data || []);
      setNotaries(notariesRes?.data || []);
    } catch (error) {
      console.error('Error fetching form data:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.property_id) {
      newErrors.property_id = 'Propriété est requise';
    }
    
    if (!formData?.client_id) {
      newErrors.client_id = 'Client est requis';
    }

    if (!formData?.appointment_type?.trim()) {
      newErrors.appointment_type = 'Type de rendez-vous est requis';
    }

    if (!formData?.scheduled_date) {
      newErrors.scheduled_date = 'Date et heure sont requises';
    } else {
      const appointmentDate = new Date(formData?.scheduled_date);
      const now = new Date();
      if (appointmentDate < now) {
        newErrors.scheduled_date = 'La date ne peut pas être dans le passé';
      }
    }

    if (!formData?.duration_minutes || formData?.duration_minutes < 15) {
      newErrors.duration_minutes = 'Durée doit être d\'au moins 15 minutes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);

      const appointmentData = {
        ...formData,
        scheduled_date: new Date(formData?.scheduled_date)?.toISOString(),
        property_id: formData?.property_id || null,
        client_id: formData?.client_id || null,
        agent_id: formData?.agent_id || null,
        notary_id: formData?.notary_id || null
      };

      if (appointment?.id) {
        // Update existing appointment
        const { error } = await supabase?.from('appointments')?.update({
            ...appointmentData,
            updated_at: new Date()?.toISOString()
          })?.eq('id', appointment?.id);

        if (error) throw error;
      } else {
        // Create new appointment
        const { error } = await supabase?.from('appointments')?.insert([appointmentData]);

        if (error) throw error;
      }

      onSave?.();
      alert(appointment ? 'Rendez-vous mis à jour avec succès' : 'Rendez-vous créé avec succès');
    } catch (error) {
      console.error('Error saving appointment:', error);
      alert('Erreur lors de la sauvegarde: ' + (error?.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-90vh overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold">
            {appointment ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous'}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Propriété *
              </label>
              <select
                value={formData?.property_id}
                onChange={(e) => {
                  const selectedProperty = properties?.find(p => p?.id === e?.target?.value);
                  setFormData(prev => ({ 
                    ...prev, 
                    property_id: e?.target?.value,
                    location: selectedProperty?.address || prev?.location
                  }));
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.property_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner une propriété</option>
                {properties?.map((property) => (
                  <option key={property?.id} value={property?.id}>
                    {property?.title} - {property?.address}
                  </option>
                ))}
              </select>
              {errors?.property_id && <p className="text-red-500 text-sm mt-1">{errors?.property_id}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client *
              </label>
              <select
                value={formData?.client_id}
                onChange={(e) => setFormData(prev => ({ ...prev, client_id: e?.target?.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.client_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Sélectionner un client</option>
                {clients?.map((client) => (
                  <option key={client?.id} value={client?.id}>
                    {client?.full_name} ({client?.email})
                  </option>
                ))}
              </select>
              {errors?.client_id && <p className="text-red-500 text-sm mt-1">{errors?.client_id}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agent
              </label>
              <select
                value={formData?.agent_id}
                onChange={(e) => setFormData(prev => ({ ...prev, agent_id: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Aucun agent assigné</option>
                {agents?.map((agent) => (
                  <option key={agent?.id} value={agent?.id}>
                    {agent?.full_name} ({agent?.email})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notaire
              </label>
              <select
                value={formData?.notary_id}
                onChange={(e) => setFormData(prev => ({ ...prev, notary_id: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Aucun notaire assigné</option>
                {notaries?.map((notary) => (
                  <option key={notary?.id} value={notary?.id}>
                    {notary?.user?.full_name} ({notary?.license_number})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de rendez-vous *
              </label>
              <select
                value={formData?.appointment_type}
                onChange={(e) => setFormData(prev => ({ ...prev, appointment_type: e?.target?.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.appointment_type ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="Property Visit">Visite de propriété</option>
                <option value="Property Evaluation">Évaluation de propriété</option>
                <option value="Contract Signing">Signature de contrat</option>
                <option value="Document Review">Révision de documents</option>
                <option value="Final Settlement">Règlement final</option>
                <option value="Consultation">Consultation</option>
              </select>
              {errors?.appointment_type && <p className="text-red-500 text-sm mt-1">{errors?.appointment_type}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={formData?.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="scheduled">Programmé</option>
                <option value="confirmed">Confirmé</option>
                <option value="completed">Terminé</option>
                <option value="cancelled">Annulé</option>
                <option value="rescheduled">Reporté</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date et heure *
              </label>
              <input
                type="datetime-local"
                value={formData?.scheduled_date}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e?.target?.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.scheduled_date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors?.scheduled_date && <p className="text-red-500 text-sm mt-1">{errors?.scheduled_date}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durée (minutes) *
              </label>
              <input
                type="number"
                min="15"
                step="15"
                value={formData?.duration_minutes}
                onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e?.target?.value) }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors?.duration_minutes ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors?.duration_minutes && <p className="text-red-500 text-sm mt-1">{errors?.duration_minutes}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lieu
            </label>
            <input
              type="text"
              value={formData?.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Lieu du rendez-vous (par défaut: adresse de la propriété)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes générales
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Notes générales sur le rendez-vous..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes du client
              </label>
              <textarea
                value={formData?.client_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, client_notes: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={2}
                placeholder="Notes spécifiques du client..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes de l'agent
              </label>
              <textarea
                value={formData?.agent_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, agent_notes: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={2}
                placeholder="Notes internes de l'agent..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Sauvegarde...' : (appointment ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentForm;